import { useLoader, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { Clone, MeshTransmissionMaterial } from '@react-three/drei'
import { useRef, useEffect, useMemo } from 'react'
import { useControls } from 'leva'

import { loadModel } from './loadModel'
import {
  registerProductObject, selectedProductStore, hoveredProductStore, isUiOpen, useStore,
} from '../../store/uiStore'
import { IS_TOUCH } from '../../config/touch'
import { cfgVita } from '../../config/vita'
import { semeDaId } from '../../config/scaffali'

// ============================================================
// Rappresentazione 3D di UN prodotto sullo scaffale:
//  - ProductBoxModel  → scatola generica (o GLB dedicato) + anello sconto
//  - ProductCustomModel → GLB caricato tra i media Shopify, normalizzato
//  - DiscountRing → scritta sconto ripetuta in cerchio che ruota
// I modelli di SCENA (negozio, piante, vetri) stanno in ModelsHandler.
// ============================================================

// --- Anello sconto: banda cilindrica con CanvasTexture ripetuta,
// 1 draw call. Sparisce quando il prodotto è in vetrina. ---
const ringTextureCache = new Map()
function getDiscountRingTexture(text) {
  if (ringTextureCache.has(text)) return ringTextureCache.get(text)
  const c = document.createElement('canvas')
  c.width = 256
  c.height = 72
  const ctx = c.getContext('2d')
  ctx.font = 'bold 56px Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // giallo con bordo rosso, stile cartellone "SALDI"
  ctx.lineWidth = 9
  ctx.strokeStyle = '#d32f2f'
  ctx.strokeText(text, 128, 38)
  ctx.fillStyle = '#ffd60a'
  ctx.fillText(text, 128, 38)
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = THREE.RepeatWrapping
  tex.repeat.x = 9 // quante volte la scritta si ripete lungo il cerchio
  tex.colorSpace = THREE.SRGBColorSpace
  ringTextureCache.set(text, tex)
  return tex
}

function DiscountRing({ text }) {
  const ref = useRef()
  const texture = useMemo(() => getDiscountRingTexture(text), [text])

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.7
  })

  return (
    <mesh ref={ref} position={[0, 0.05, 0]}>
      {/* cilindro aperto; FrontSide = si legge solo il lato rivolto
          a noi, niente testo specchiato in trasparenza */}
      <cylinderGeometry args={[0.42, 0.42, 0.19, 40, 1, true]} />
      <meshBasicMaterial map={texture} transparent side={THREE.FrontSide} depthWrite={false} />
    </mesh>
  )
}

// Modello 3D dedicato del prodotto (media MODEL_3D di Shopify):
// i GLB arrivano con scale/origini arbitrarie → si normalizza:
// dimensione massima = slider "scala modelli 3D", centrato nel group.
// IMPORTANTE: la misura va fatta sul GLB "vergine" e la trasformazione
// applicata a un GROUP contenitore, mai all'oggetto stesso — il GLB è
// in cache condivisa e scalarlo direttamente falsa le misure successive
// (scala che cambia tra un montaggio e l'altro).
function ProductCustomModel({ url }) {
  const { scalaModelli3D } = useControls('Scaffali', {
    scalaModelli3D: { value: 0.8, min: 0.2, max: 3, step: 0.05, label: 'scala modelli 3D' },
  }, { collapsed: true })
  const gltf = loadModel(url)

  const { scale, offset } = useMemo(() => {
    // azzera eventuali trasformazioni residue prima di misurare
    gltf.scene.position.set(0, 0, 0)
    gltf.scene.scale.set(1, 1, 1)
    gltf.scene.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(gltf.scene)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z) || 1
    const s = scalaModelli3D / maxDim
    return { scale: s, offset: [-center.x * s, -center.y * s, -center.z * s] }
  }, [gltf, scalaModelli3D])

  return (
    <group scale={scale} position={offset}>
      <primitive object={gltf.scene} />
    </group>
  )
}

export function ProductBoxModel({ image, position, rotationY = 0, scale = 1, discountText, product }) {
  const texture = useLoader(THREE.TextureLoader, image)

  // L'anello sconto gira solo finché il prodotto è sullo scaffale:
  // quando è in vetrina (selezionato) sparisce
  const selected = useStore(selectedProductStore)
  const inMano = selected?.id === product.id

  // Registra il group per la vetrina 3D (ProductShowcase lo anima
  // dallo scaffale alla camera quando si apre la pagina prodotto)
  const groupRef = useRef()
  useEffect(() => {
    registerProductObject(product.id, groupRef.current)
    return () => registerProductObject(product.id, null)
  }, [product.id])

  // --- LA VITA DELLA SCATOLA (respiro + reazione al mirino, vedi
  // config/vita.js). STA SU UN GRUPPO INTERNO, non su quello registrato
  // qui sopra: a quello ProductShowcase scrive posizione, rotazione e
  // scala a ogni frame quando il prodotto va in vetrina, e si ricorda la
  // posa di casa per rimettercelo. Animare lo stesso nodo vorrebbe dire
  // litigarci, e la scatola tornerebbe sullo scaffale nel posto
  // sbagliato. (Stessa regola delle tre animazioni della mascotte: un
  // movimento, un appiglio.)
  const vitaRef = useRef()
  const seme = useMemo(() => semeDaId(product.id), [product.id])
  const mira = useRef(0) // 0..1, quanto è "puntata" adesso

  useFrame((state, delta) => {
    const g = vitaRef.current
    if (!g) return
    const k = 1 - Math.exp(-cfgVita.miraVelocita * delta) // indipendente dagli FPS

    // In vetrina comanda ProductShowcase (e il pomello che la fa girare):
    // il respiro si ritira, o si sommerebbe alla posa della vetrina.
    if (inMano || !cfgVita.attiva) {
      mira.current += -mira.current * k
      g.position.y += -g.position.y * k
      g.rotation.y += -g.rotation.y * k
      g.rotation.z += -g.rotation.z * k
      g.scale.setScalar(g.scale.x + (1 - g.scale.x) * k)
      return
    }

    const puntata = !isUiOpen() && hoveredProductStore.get()?.id === product.id
    mira.current += ((puntata ? 1 : 0) - mira.current) * k
    const m = mira.current

    // Fase e ritmo propri di questa scatola (dal seme del suo id) e due
    // frequenze incommensurabili: sommate non si ripetono a occhio, e
    // due scatole vicine non vanno mai a tempo.
    const t = (state.clock.elapsedTime + seme * 37) * cfgVita.ritmo * (0.9 + seme * 0.2)
    const su = 0.5 - 0.5 * Math.cos(t * 2.1) // 0..1: solo verso l'alto
    g.position.y = su * cfgVita.respiro * (1 + m) + m * cfgVita.miraAlza
    g.rotation.y = Math.sin(t * 1.27 + seme * 6.28) * cfgVita.dondolio * (1 + 1.5 * m)
    g.rotation.z = Math.sin(t * 0.83 + 2.1) * cfgVita.dondolio * 0.45
    g.scale.setScalar(1 + m * cfgVita.miraIngrandisce)
  })

  const bakedTexture = useLoader(THREE.TextureLoader, 'textures/bakedPackage.jpg')

  // Materiale MEMOIZZATO: ogni selezione fa ri-renderizzare tutte le
  // scatole (useStore) — senza memo si creava un materiale nuovo (e si
  // ri-traversava il clone) a ogni render, per niente
  const bakedMaterial = useMemo(() => {
    bakedTexture.flipY = false
    bakedTexture.colorSpace = THREE.SRGBColorSpace
    return new THREE.MeshBasicMaterial({
      map: bakedTexture,
      side: THREE.DoubleSide,
      color: 0xE5E4E2
    })
  }, [bakedTexture])

  const pruductBoxModel = loadModel('models/productPackage-draco.glb')
  pruductBoxModel.scene.children[0].material = bakedMaterial

  const config = useControls('Plastic Material', {
    samples: { value: 1, min: 1, max: 32, step: 1 },
    // ATTENZIONE PERFORMANCE: ogni scatola ri-renderizza la scena in un
    // buffer resolution² A OGNI FRAME per la trasmissione → con N
    // prodotti il costo scala N volte. 256 è indistinguibile su un
    // pannello così piccolo e costa 1/16 dei pixel di 1024.
    resolution: { value: 256, min: 128, max: 2048, step: 128 },
    transmission: { value: 1, min: 0, max: 1 },
    roughness: { value: 0.0, min: 0, max: 1, step: 0.01 },
    thickness: { value: 0.08, min: 0, max: 10, step: 0.01 },
    ior: { value: 1, min: 1, max: 5, step: 0.01 },
    chromaticAberration: { value: 0.01, min: 0, max: 1 },
    anisotropicBlur: { value: 0.1, min: 0, max: 1, step: 0.01 },
    distortion: { value: 0.0, min: 0, max: 1, step: 0.01 },
    distortionScale: { value: 0.3, min: 0.01, max: 1, step: 0.01 },
    temporalDistortion: { value: 0.5, min: 0, max: 1, step: 0.01 },
    clearcoat: { value: 1, min: 0, max: 1 },
    attenuationDistance: { value: 0.43, min: 0, max: 10, step: 0.01 },
    attenuationColor: '#fcfcfc',
    color: '#ffffff',
  }, { collapsed: true })

  // GLB dedicato caricato tra i media Shopify del prodotto (se c'è,
  // sostituisce la scatola generica)
  const model3dUrl = product?.media?.edges
    ?.find((e) => e.node.mediaContentType === 'MODEL_3D')
    ?.node.sources?.find((s) => s.format === 'glb')?.url || null

  return <>
    {/* userData.product → letto dal ProductRaycaster (mirino + tasto E) */}
    <group ref={groupRef} position={position} rotation={[0, rotationY, 0]} scale={scale} userData={{ product }}>
      {/* gruppo della "vita": respira e reagisce al mirino */}
      <group ref={vitaRef}>
      {model3dUrl ? (
        <ProductCustomModel url={model3dUrl} />
      ) : (
        <>
          <Clone
            object={pruductBoxModel.scene}
            scale={1.2}
            onClone={(cloned) => {
              cloned.traverse((child) => {
                if (child.isMesh) child.material = bakedMaterial
              })
            }}
          />
          <mesh position={[0, -0.04, 0.19]} scale={[0.5, 0.73, 0.5]}>
            <planeGeometry />
            <meshBasicMaterial map={texture} />
          </mesh>
          {/* Strato "plastica" a trasmissione: troppo pesante per le GPU
              mobili → sui touch si salta (resta scatola + immagine) */}
          {!IS_TOUCH && (
            <mesh position={[0, -0.03, 0.2]} scale={[0.5, 0.7, 0.5]}>
              <planeGeometry />
              <MeshTransmissionMaterial background={new THREE.Color(config.bg)} {...config} />
            </mesh>
          )}
        </>
      )}
      </group>
      {/* l'anello sconto sta FUORI dal gruppo della vita: è un cartello
          che gira attorno al prodotto, e ondeggiando con lui sembrerebbe
          attaccato alla scatola invece che sospeso */}
      {discountText && !inMano && (
        <DiscountRing text={discountText} />
      )}
    </group>
  </>
}
